/**
 * シフト（予定）タブの「どう読ませるか」だけを決める（R0102 B-2）。
 *
 * ★`src/ui/punch-view.ts` と同じ理由で DOM から切り離す。**判断をここだけに置く**
 *   （描画側＝`src/ui/main.ts` は文字列を DOM へ写すだけにする）。
 *
 * ## ★24時超えの時刻表現（`docs/02` §4.4）
 *
 * 予定の開始・終了・休憩は「所属営業日の0時起点の分」。深夜2時の終了は **26:00 = 1560分**。
 * ★**`<input type="time">` は 23:59 までしか表せない**ので使えない。
 *   `HH:MM` の文字列として受け、ここで分へ直す。
 *   ★値域（`[0, 2880)`）と対の規則の**最後の砦はサーバ**（`src/infra/shifts.ts` と
 *   `migrations/0051`）。ここで弾くのは、押した直後に理由を出すため。
 *
 * ## ★氏名は「サーバが返したものを写すだけ」（B-5）
 *
 * 2026-09-01（B-5）から応答に氏名が載る（`docs/05` §3.7 の例外・依頼者判断 Q-S1）。
 * ★**それでも画面で名前を作らない**——従業員名簿と突き合わせて合成すると、
 * 可視性の境界が画面側へ逃げる。載っている `displayName` をそのまま写すだけにする。
 * ★**「自分の分だけ」は表示の絞り込み**であって可視性の境界ではない
 * （全ロールが店舗全体を読める＝§3.7）。
 */

/** ［開始, 終了）の対 */
export interface ShiftBreakLike {
  readonly startMinutes: number;
  readonly endMinutes: number;
}

export interface ShiftLike {
  readonly businessDate: string;
  readonly startMinutes: number;
  readonly endMinutes: number;
  readonly breaks: readonly ShiftBreakLike[];
}

/**
 * `"26:00"` → `1560`。読めなければ `null`。
 * ★**24時以降も受ける。** 24時で切ると深夜勤務が入力できない。
 */
export function parseHhmm(value: string): number | null {
  const m = /^(\d{1,2}):([0-5]\d)$/.exec(value.trim());
  if (m === null) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** `1560` → `"26:00"`。★24で割らない（翌日の 02:00 に見せると営業日がずれて読める） */
export function formatHhmm(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/** `"2026-09-05"` → `"9/5"`。★1行を 390px に収めるため */
export function formatShiftDate(businessDate: string): string {
  const [, m, d] = businessDate.split("-");
  return `${Number(m)}/${Number(d)}`;
}

/**
 * 表示中の月（`YYYY-MM`）を読める形にする（R0102 B-8）。
 * ★**年を落とさない。** 12月に翌1月を開く運用があるので、「1月」だけでは去年と区別できない。
 */
export function formatShiftMonth(month: string): string {
  const [y, m] = month.split("-");
  return `${y}年${Number(m)}月`;
}

/**
 * 1件の要約。★休憩は**対のまま**並べる（合計分に畳むと中抜けが消える＝`docs/02` §4.4）。
 * ★氏名は含めない（B-2）。
 */
export function shiftSummary(s: ShiftLike): string {
  const base = `${formatHhmm(s.startMinutes)}〜${formatHhmm(s.endMinutes)}`;
  if (s.breaks.length === 0) return base;
  const breaks = s.breaks
    .map((b) => `${formatHhmm(b.startMinutes)}〜${formatHhmm(b.endMinutes)}`)
    .join(" / ");
  return `${base}（休憩 ${breaks}）`;
}

/**
 * 営業日ごとにまとめる（B-5 AC-8）。★**表の見出しを日付にするため。**
 *
 * 20名×1か月を1行ずつ並べると 390px では日付が何百回も繰り返される。
 * ★**並び順はサーバの `ORDER BY` を尊重して崩さない**（Map の挿入順のまま返す）——
 * ここで並べ替えると、順序の持ち主が2箇所になる。
 */
export function groupByDate<T extends { readonly businessDate: string }>(
  shifts: readonly T[],
): { businessDate: string; shifts: T[] }[] {
  const byDate = new Map<string, T[]>();
  for (const s of shifts) {
    const bucket = byDate.get(s.businessDate);
    if (bucket === undefined) byDate.set(s.businessDate, [s]);
    else bucket.push(s);
  }
  return [...byDate].map(([businessDate, group]) => ({ businessDate, shifts: group }));
}

/**
 * 申請の状態を日本語にする（R0102 B-3）。★**画面で状態を作らない**——
 * サーバが返す4値をそのまま写す。増えたら `tsc` が落ちる形にしておく。
 */
export function requestStatusLabel(
  status: "SUBMITTED" | "APPROVED" | "REJECTED" | "WITHDRAWN",
): string {
  const labels = {
    SUBMITTED: "承認待ち",
    APPROVED: "承認済み",
    REJECTED: "却下",
    WITHDRAWN: "取り下げ",
  } as const;
  return labels[status];
}
